import { getClientTraceHeaders } from "./clientTraceHeaders";

type ApiFetchInit = RequestInit & { _retry?: boolean };

type RefreshResult = { ok: boolean; reason?: string | null };

let refreshPromise: Promise<RefreshResult> | null = null;

async function tryRefresh(): Promise<RefreshResult> {
  try {
    const r = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (r.ok) return { ok: true };
    const data = await r.json().catch(() => null);
    return { ok: false, reason: data?.reason || null };
  } catch {
    return { ok: false };
  }
}

async function refreshOnce(): Promise<RefreshResult> {
  if (!refreshPromise) {
    refreshPromise = tryRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function getUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export async function apiFetch(
  input: RequestInfo | URL,
  init: ApiFetchInit = {}
): Promise<Response> {
  const url = getUrl(input);

  const disable401Interceptor = (() => {
    try {
      if (typeof window === "undefined") return false;
      const params = new URLSearchParams(window.location.search);
      if (params.get("no401") === "1") return true;
      return localStorage.getItem("ww_disable_401_interceptor") === "1";
    } catch {
      return false;
    }
  })();

  const headers = new Headers(init.headers || undefined);
  const trace = getClientTraceHeaders();
  Object.entries(trace).forEach(([k, v]) => {
    if (!headers.has(k)) headers.set(k, v);
  });

  const response = await fetch(input, {
    ...init,
    headers,
    credentials: init.credentials ?? "include",
  });

  if (disable401Interceptor) return response;

  const isRefreshEndpoint = url.includes("/api/auth/refresh");
  if (response.status !== 401 || init._retry || isRefreshEndpoint) {
    return response;
  }

  const refreshResult = await refreshOnce();
  if (!refreshResult.ok) {
    // Fail-safe: при потере сессии всегда переводим на логин с явным маркером причины,
    // чтобы не оставлять пользователя в "белом экране" или бесконечной загрузке.
    const p = window.location.pathname;
    if (p !== "/login" && p !== "/registration") {
      if (refreshResult.reason === "active_session_not_found") {
        window.location.assign("/login?session=active_session_not_found");
        throw new Error("Unauthorized");
      }
      window.location.assign("/login?session=expired");
    }
    throw new Error("Unauthorized");
  }

  return apiFetch(input, { ...init, _retry: true });
}

